<template>
  <div class="formPanel">
    <div v-for="item in panelElements" :key="item.ElementId">
      <div
        v-if="
          item.FieldType === 'Lookup' ||
          item.FieldType === 'PlanLookup' ||
          item.FieldType === 'LookupList' ||
          item.FieldType === 'PlanList'
        "
      >
        <Dropdown
          :label="item.Label"
          v-model="item.Value"
          :listItems="item.LookupItems"
          :readonly="readOnly"
          :rules="getRules(item)"
          :valueName="item.Type === 'int' ? 'Id' : 'Value'"
          :disabled="
            editDisabled && editDisabled.length ? editDisabled.includes(item.FieldId) : false
          "
          :prepend-icon="item.Attributes === 'addressLink' ? 'mdi-contacts' : ''"
          @click:prepend="
            () => (item.Attributes === 'addressLink' ? goToAddressBook(item.Value) : null)
          "
          :title="item.FieldName"
          @click="handleClick($event, item)"
        >
        </Dropdown>
      </div>
      <div v-else-if="item.FieldType === 'List'">
        <Dropdown
          :label="item.Label"
          v-model="item.Value"
          :listItems="getListItems(item.ListItems)"
          :readonly="readOnly"
          :rules="getRules(item)"
          :disabled="
            editDisabled && editDisabled.length ? editDisabled.includes(item.FieldId) : false
          "
          :title="item.FieldName"
          @click="handleClick($event, item)"
        ></Dropdown>
      </div>
      <div v-else-if="item.FieldType === 'Radio'">
        <v-radio-group
          v-model="item.Value"
          row
          :readonly="readOnly"
          :label="item.Label"
          :title="item.FieldName"
        >
          <v-radio
            v-for="radioOption in getRadioOptions(item)"
            :key="radioOption.value"
            :label="radioOption.label"
            :value="radioOption.value"
            class="small-radio"
            @click="handleClick($event, item)"
          ></v-radio>
        </v-radio-group>
      </div>
      <div v-else-if="item.FieldType === 'CheckBox' && item.Type === 'char'">
        <v-checkbox
          v-model="item.Value"
          :label="item.Label"
          :readonly="readOnly"
          :rules="getRules(item)"
          true-value="Y"
          false-value="N"
          :title="item.FieldName"
          @click="handleClick($event, item)"
        ></v-checkbox>
      </div>
      <div v-else-if="item.FieldType === 'CheckBox'">
        <v-checkbox
          v-model="item.Value"
          :label="item.Label"
          :readonly="readOnly"
          :rules="getRules(item)"
          :title="item.FieldName"
          @click="handleClick($event, item)"
        ></v-checkbox>
      </div>
      <div v-else-if="item.FieldType === 'datetime' || item.FieldClass === 'control_date'">
        <DatePicker
          v-model="item.Value"
          :label="item.Label"
          :readonly="readOnly || item.ReadOnly"
          :rules="getRules(item)"
          :title="item.FieldName"
          @click="handleClick($event, item)"
        ></DatePicker>
      </div>
      <div v-else-if="item.FieldType === 'Telephone'">
        <v-text-field
          :readonly="readOnly"
          :label="item.Label"
          :rules="getRules(item)"
          v-model="item.Value"
          dense
          outlined
          :title="item.FieldName"
          @click="handleClick($event, item)"
        >
          <template v-slot:append>
            <a :href="`callto:${item.Value}`" target="_blank">
              <v-icon>mdi-phone-forward</v-icon>
            </a>
          </template>
        </v-text-field>
      </div>
      <div v-else-if="item.FieldType === 'Email'">
        <v-text-field
          :readonly="readOnly"
          :label="item.Label"
          :rules="getRules(item)"
          v-model="item.Value"
          dense
          outlined
          :title="item.FieldName"
          @click="handleClick($event, item)"
        >
          <template v-slot:append>
            <a :href="`mailto:${item.Value}`" target="_blank">
              <v-icon>mdi-email-arrow-right</v-icon>
            </a>
          </template>
        </v-text-field>
      </div>
      <div v-else-if="item.FieldType === 'string'">
        <div v-if="isCalculationSelection(item)">
          <v-text-field
            :readonly="readOnly"
            :label="item.Label"
            v-model="item.Value"
            :rules="getRules(item)"
            append-icon="mdi-arrow-down-bold-box"
            @click:append="openCalculationSelection(item.FieldName)"
            dense
            outlined
            :title="item.FieldName"
            @click="handleClick($event, item)"
          ></v-text-field>
        </div>
        <div v-else>
          <v-text-field
            :readonly="readOnly"
            :label="item.Label"
            v-model="item.Value"
            :rules="getRules(item)"
            :disabled="
              editDisabled && editDisabled.length ? editDisabled.includes(item.FieldId) : false
            "
            dense
            outlined
            @blur="item.onChange == undefined ? null : item.onChange($event)"
            :title="item.FieldName"
            @click="handleClick($event, item)"
          ></v-text-field>
        </div>
      </div>
      <div v-else-if="item.FieldType === 'Address'">
        <v-textarea
          :readonly="readOnly"
          :label="item.Label"
          v-model="item.Value"
          :rules="getRules(item)"
          dense
          outlined
          :title="item.FieldName"
          @click="handleClick($event, item)"
        >
        </v-textarea>
      </div>
      <div v-else-if="item.FieldType === 'numeric'">
        <v-text-field
          type="number"
          min="0"
          :readonly="readOnly"
          :label="item.Label"
          v-model="item.Value"
          :rules="getRules(item)"
          :step="getNumericStep(item)"
          :disabled="
            editDisabled && editDisabled.length ? editDisabled.includes(item.FieldId) : false
          "
          dense
          outlined
          :title="item.FieldName"
          @click="handleClick($event, item)"
        ></v-text-field>
      </div>
      <div v-else-if="item.FieldType === 'StatusHistory'" class="mt-5 mb-10">
        <span>Status History</span>
        <status-history :history="item.LookupItems"> </status-history>
      </div>
      <div v-else-if="item.FieldType === 'PensionElements'">
        <span>Pension Elements</span>
      </div>
      <div v-else-if="item.FieldType === 'Photo'">
        <img width="100%" :src="imageSource" />
      </div>
      <div v-else-if="item.FieldType === 'Signature'">
        <img width="100%" :src="signatureSource" />
      </div>
      <div v-else-if="item.FieldType === 'Header'" class="my-4">
        <h4 class="text-sm font-normal" style="color: grey">
          {{ item.Label }}
        </h4>
      </div>
    </div>
    <v-dialog v-model="dialog" width="300">
      <v-card>
        <v-card-title class="pb-5">
          <span>Field Properties</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            label="Field Name"
            :value="selectedField.FieldName"
            disabled
            dense
          ></v-text-field>
          <v-text-field
            label="Field Label"
            :value="selectedField.Label"
            disabled
            dense
          ></v-text-field>
          <v-text-field
            label="Field Order"
            :value="selectedField.FieldOrder"
            disabled
            dense
          ></v-text-field>
          <v-checkbox label="Visible" v-model="selectedField.Visible" disabled dense> </v-checkbox>
          <v-checkbox label="Mandatory" v-model="selectedField.Mandatory" disabled dense>
          </v-checkbox>
          <v-checkbox label="Plan Level" v-model="selectedField.PlanId" disabled dense>
          </v-checkbox>
          <v-text-field
            label="Field Class"
            :value="selectedField.LabelClass"
            disabled
            dense
          ></v-text-field>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DatePicker from '@/components/formControls/DatePicker.vue';
import Dropdown from '@/components/formControls/Dropdown.vue';
import Utils from '@/utils.js';
import StatusHistory from '../views/Members/Components/StatusHistory.vue';

export default {
  name: 'FormPanel',
  components: {
    DatePicker,
    Dropdown,
    StatusHistory,
  },
  props: {
    panelName: { type: String },
    parentContainer: { type: String },
    formElements: {
      type: Array,
    },
    readOnly: { type: Boolean, default: true },
    editDisabled: { type: Array },
    calculationSelectionInputs: { type: Array },
  },
  data: () => ({
    numericStep: 1,
    rules: {
      required: (value) => value !== null && value !== undefined && value !== '' || 'Required.',
      counter: (value) => value.length <= 20 || 'Max 20 characters',
      email: (value) => {
        if (!value) return true;
        const pattern =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return pattern.test(value) || 'Invalid e-mail.';
      },
      phone: (v) => {
        if (!v) return true;
        else {
          const phoneNumber = v.replace(/ /g, '');
          const pattern = /^\+?\d{6,16}$/;
          if (pattern.test(phoneNumber)) return true;
          //if (phoneNumber.length == 8 || phoneNumber.length == 11) return true;

          return 'Please enter correct phone number.';
        }
      },
    },
    dialog: false,
    selectedField: {},
  }),
  methods: {
    handleClick(event, item) {
      if (event.ctrlKey) {
        this.selectedField = item;
        this.dialog = true;
      }
    },

    getListItems(listItems) {
      var list = listItems.split('|').map((item) => item.trim());
      return list;
    },
    getRadioOptions(item) {
      const result = [];
      if (item) {
        const options = item.ListItems.split('|');
        if (options) {
          options.forEach((x) => {
            const optionParams = x.split(';');
            result.push({
              label: optionParams[1],
              value: optionParams[0],
            });
          });
        }
      }

      return result;
    },
    getRules(item) {
      const result = [];
      if (item.Mandatory) result.push(this.rules.required);
      if (item.FieldType === 'Email') {
        result.push(this.rules.email);
      }
      if (item.FieldType === 'Telephone') result.push(this.rules.phone);

      return result;
    },
    getNumericStep(item) {
      switch (item.FieldFormat) {
        case '{0:0.00}':
          return 0.01;
        case '{0:0.0}':
          return 0.01;
        default:
          return 1;
      }
    },
    isCalculationSelection(formElement) {
      return (
        this.calculationSelectionInputs &&
        this.calculationSelectionInputs.length > 0 &&
        this.calculationSelectionInputs.includes(formElement.FieldName)
      );
    },
    openCalculationSelection(fieldName) {
      if (!this.readOnly) this.$emit('open-calculation-selection', fieldName);
    },
    goToAddressBook(name) {
      var link = '/AddressBook?name=' + name;
      window.location.href = link;
    },
  },
  computed: {
    imageSource: function () {
      var memno = this.$store.state.selectedMemberId;
      return (
        this.$config.VUE_APP_BASE_URL + `/member/getimage?membershipid=${memno}&signature=false`
      );
    },
    signatureSource: function () {
      var memno = this.$store.state.selectedMemberId;
      return this.$config.VUE_APP_BASE_URL + `/member/getimage?membershipid=${memno}&signature=true`;
    },
    panelElements: function () {
      let result = [];
      if (!this.panelName) result = [...this.formElements];
      else {
        const filteredArray = this.formElements.filter(
          (f) => f.ParentContainer === this.panelName || f.ParentContainer === this.parentContainer
        );
        result = [...filteredArray];
      }

      result = result.sort(Utils.dynamicSort('FieldOrder'));
      result = result.sort(Utils.dynamicSort('FieldOrder'));

      return result;
    },
  },
};
</script>

<style lang="scss" scoped>
.formPanel {
  margin-top: 5px;

  .v-input--checkbox,
  .v-input--radio-group--row {
    margin-top: 8px;
    margin-left: 5px;
    margin-bottom: 10px;
  }
  .v-input {
    max-width: 420px;
  }
}
</style>
